import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { API, useAuth } from "../context/AuthContext";

/**
 * Payment success page.
 *
 * Contract (production-critical):
 *   1. When Stripe redirects back here, we IMMEDIATELY call
 *      POST /api/subscription/activate-from-session which synchronously
 *      writes the subscription state onto the user record (bypassing any
 *      reconcile rate-limit / webhook lag).
 *   2. If activation returns subscription_active=true, we redirect to
 *      /business AFTER refreshing the auth-me record so the dashboard sees
 *      the new state on first render.
 *   3. If activation returns false (rare race — Stripe hasn't yet linked the
 *      subscription to the session), we retry every 2s for up to 30 attempts.
 *      Between retries we ALSO poll /auth/me — the customer.subscription.created
 *      webhook may flip the user first.
 *   4. There is NO "Skip" escape hatch. The activation must complete before
 *      the user reaches their dashboard so they never see "Subscription
 *      inactive" on /business right after paying.
 *   5. If activation ultimately fails (Stripe unreachable / expired key), we
 *      surface the exact error so the user knows to contact support instead
 *      of being silently trapped.
 */

const RETRY_DELAY_MS = 2000;
const MAX_ATTEMPTS = 30; // ~60s of active retries
const FRIENDLY_DELAY_AFTER_MS = 12 * 1000; // Copy softens after 12s

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const type = params.get("type") || "subscription";
  const bookingId = params.get("booking_id");
  const { refreshUser, token, user } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState(
    params.get("type") === "booking_setup" ? "Saving your card securely — no charge today…"
      : params.get("type") === "booking" ? "Confirming your booking…"
      : "Activating your subscription…");
  const [detail, setDetail] = useState(null);
  const [failed, setFailed] = useState(null);
  const [done, setDone] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const attempts = useRef(0);
  const startedAt = useRef(Date.now());
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    if (!sessionId) {
      setFailed("Missing session id — please return to the dashboard and try again.");
      return;
    }
    if (!token) {
      // AuthContext still loading — try again once available.
      return;
    }

    const authHeaders = () => ({ Authorization: `Bearer ${token}` });
    const isSubscription = type === "subscription";
    const isBookingSetup = type === "booking_setup";

    const finish = async (destination) => {
      if (cancelledRef.current) return;
      setDone(true);
      setStatus(type === "booking_setup"
        ? "Card saved — taking you to your appointments…"
        : "Subscription active — taking you to your dashboard…");
      try { await refreshUser(); } catch (_) { /* best-effort */ }
      setTimeout(() => {
        if (!cancelledRef.current) nav(destination, { replace: true });
      }, 700);
    };

    const callActivate = async () => {
      try {
        const r = await axios.post(
          `${API}/subscription/activate-from-session`,
          { session_id: sessionId },
          { headers: authHeaders() },
        );
        return r.data;
      } catch (e) {
        const s = e?.response?.status;
        if (s === 401 || s === 403 || s === 404) {
          // Terminal — surface the error
          setFailed(e?.response?.data?.detail || "We couldn't verify your checkout session.");
          return null;
        }
        // Transient network / 5xx — treat as retryable
        return { transient_error: e?.response?.data?.detail || e?.message || "Network error", subscription_active: false };
      }
    };

    const pollAuthMe = async () => {
      if (!token) return null;
      try {
        const r = await axios.get(`${API}/auth/me`, { headers: authHeaders() });
        return r.data;
      } catch (_) { return null; }
    };

    const tick = async () => {
      if (cancelledRef.current) return;
      attempts.current += 1;

      // Cashless card-save flow: nothing is charged, so poll the booking until
      // the saved card lands (webhook) instead of a payment status.
      if (isBookingSetup) {
        try {
          const r = await axios.get(`${API}/customer/bookings`, { headers: authHeaders() });
          const bk = (r.data || []).find((b) => b.id === bookingId);
          if (bk && bk.remaining_balance_status && bk.remaining_balance_status !== "setup_pending") {
            return finish("/account");
          }
        } catch (_) { /* keep polling */ }
        if (attempts.current >= MAX_ATTEMPTS) {
          setFailed("Your card was saved — your booking will appear in My Appointments shortly.");
          return;
        }
        setStatus("Saving your card securely — no charge today…");
        setTimeout(tick, RETRY_DELAY_MS);
        return;
      }

      // Non-subscription (legacy booking payments) — fall back to old status endpoint.
      if (!isSubscription) {
        try {
          const r = await axios.get(`${API}/payments/status/${sessionId}`, { headers: authHeaders() });
          const ps = r.data?.payment_status;
          const s = r.data?.status;
          if (ps === "paid" || s === "complete") return finish("/account");
          if (s === "expired") {
            setFailed("Payment session expired. Please start checkout again.");
            return;
          }
        } catch (_) { /* keep polling */ }

        if (attempts.current >= MAX_ATTEMPTS) {
          setFailed("We're still confirming your payment. Your dashboard will reflect the change shortly.");
          return;
        }
        setTimeout(tick, RETRY_DELAY_MS);
        return;
      }

      // Subscription path — force-activate synchronously.
      const r = await callActivate();
      if (cancelledRef.current) return;
      if (!r) return; // fatal error already surfaced
      setDetail(r);

      if (r.subscription_active) {
        return finish("/business");
      }

      // Activation call returned but user not yet flipped active. Poll /auth/me
      // in case a webhook has already landed while our activate call was in flight.
      const me = await pollAuthMe();
      if (me && me.subscription_active) return finish("/business");

      // Copy transitions.
      const elapsed = Date.now() - startedAt.current;
      if (r.transient_error) {
        setStatus("Confirming with Stripe… (retrying)");
      } else if (elapsed > FRIENDLY_DELAY_AFTER_MS) {
        setStatus("Almost there — finishing your subscription setup…");
      } else {
        setStatus("Activating your subscription…");
      }

      if (attempts.current >= MAX_ATTEMPTS) {
        // Give the user a clear terminal state so we don't spin forever.
        const err = r?.stripe_error || r?.transient_error;
        setFailed(
          err
            ? `We couldn't fully activate your subscription just yet. Stripe reported: ${err}. Your card wasn't charged twice — please contact support with your session id ${sessionId} and we'll finish activation for you.`
            : "Your payment went through but activation is still pending. Please refresh your dashboard in a minute — it will reflect the change automatically."
        );
        return;
      }
      setTimeout(tick, RETRY_DELAY_MS);
    };

    tick();
    return () => { cancelledRef.current = true; };
  }, [sessionId, token, retryKey, nav, refreshUser, type, bookingId]);

  const showSpinner = !done && !failed;

  return (
    <div className="App">
      <Header hideSearch />
      <div className="max-w-xl mx-auto px-6 py-20 text-center" data-testid="payment-success-page">
        <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>
          {type === "subscription" ? "Subscription" : "Booking"}
        </div>
        <h1
          className="font-display text-4xl mt-2"
          style={{ color: "var(--gf-text)" }}
          data-testid="payment-heading"
        >
          {done ? "All set." : failed ? "Almost there" : "Confirming your payment"}
        </h1>
        {showSpinner && (
          <div className="mt-6 flex justify-center" aria-hidden="true">
            <span
              className="inline-block w-6 h-6 rounded-full animate-spin"
              style={{
                borderTop: "2px solid var(--gf-accent)",
                borderRight: "2px solid transparent",
                borderBottom: "2px solid transparent",
                borderLeft: "2px solid transparent",
              }}
            />
          </div>
        )}
        <p
          className="mt-6 text-sm leading-relaxed"
          style={{ color: "var(--gf-text-soft)" }}
          data-testid="payment-status"
        >
          {failed || status}
        </p>
        {detail?.subscription_tier && !done && !failed && (
          <div className="mt-2 text-xs" style={{ color: "var(--gf-text-muted)" }} data-testid="payment-plan-detail">
            Plan: <b>{detail.subscription_tier}</b>{detail.subscription_status ? ` · ${detail.subscription_status}` : ""}
          </div>
        )}
        {failed && (
          <div className="mt-8 flex flex-col items-center gap-3">
            <a
              href="mailto:bookfreely.support@gmail.com"
              data-testid="payment-support-link"
              className="text-sm underline"
              style={{ color: "var(--gf-accent)" }}
            >
              Contact support
            </a>
            {user && user.role === "business" && (
              <button
                data-testid="payment-retry-btn"
                onClick={() => {
                  attempts.current = 0;
                  startedAt.current = Date.now();
                  setFailed(null);
                  setStatus("Activating your subscription…");
                  setDetail(null);
                  setRetryKey((k) => k + 1);
                }}
                className="text-sm rounded-full border px-4 py-2"
                style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
              >
                Try again
              </button>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
